import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin } from 'lucide-react';
import { BRAND, NAV, SERVICES, CITIES } from '@/data/content';

export default function Footer() {
  return (
    <footer className="surface-dark text-brand-bone" data-testid="site-footer">
      <div className="container-x py-16 lg:py-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
        {/* Column 1 — brand */}
        <div>
          <Link to="/" className="flex items-center gap-3">
            <img src={BRAND.logoCircle} alt="HearthOak logo" className="h-14 w-14 object-contain" />
            <div>
              <div className="font-serif text-xl">HearthOak</div>
              <div className="font-sans text-[10px] tracking-[0.24em] uppercase text-brand-bone/60">Construction, LLC</div>
            </div>
          </Link>
          <p className="mt-5 font-sans text-sm text-brand-bone/70 leading-relaxed">
            {BRAND.tagline}. Owner-led residential construction, repairs, painting, doors, drywall, and finish carpentry across DFW.
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-4">
            <a href={BRAND.bbbUrl} target="_blank" rel="nofollow noopener noreferrer" data-testid="footer-bbb-link">
              <img
                src={BRAND.bbbSeal}
                alt="HearthOak Construction, LLC — BBB Accredited"
                className="h-16 w-auto bg-white p-1 border border-brand-bone/20"
              />
            </a>
            <img
              src="/brand/shawtech-wide-dark.webp"
              alt="Authorized Installation Partner of ShawTech Lighting"
              className="h-16 w-auto"
              loading="lazy"
              data-testid="footer-shawtech-badge"
            />
          </div>
          <p className="mt-3 text-xs font-sans text-brand-bone/60 uppercase tracking-wider">BBB Accredited · Authorized ShawTech Lighting Installation Partner</p>
        </div>

        {/* Column 2 — Services */}
        <div>
          <h4 className="font-sans text-xs uppercase tracking-[0.24em] text-brand-bone/60 mb-4">Services</h4>
          <ul className="space-y-2 font-sans text-sm">
            {SERVICES.map((s) => (
              <li key={s.slug}>
                <Link to={`/services/${s.slug}`} className="text-brand-bone/85 hover:text-brand-bone transition-colors">
                  {s.title}
                </Link>
              </li>
            ))}
            <li>
              <Link to="/seasonal-lighting" className="text-brand-bone/85 hover:text-brand-bone transition-colors" data-testid="footer-seasonal-lighting-link">
                Holiday &amp; Seasonal Lighting
              </Link>
            </li>
          </ul>
        </div>

        {/* Column 3 — Quick links */}
        <div>
          <h4 className="font-sans text-xs uppercase tracking-[0.24em] text-brand-bone/60 mb-4">Company</h4>
          <ul className="space-y-2 font-sans text-sm">
            {NAV.map((n) => (
              <li key={n.to}>
                <Link to={n.to} className="text-brand-bone/85 hover:text-brand-bone transition-colors">
                  {n.label}
                </Link>
              </li>
            ))}
            <li>
              <Link to="/estimate" className="text-brand-bone/85 hover:text-brand-bone transition-colors">Request an Estimate</Link>
            </li>
          </ul>
        </div>

        {/* Column 4 — Contact / Service Areas */}
        <div>
          <h4 className="font-sans text-xs uppercase tracking-[0.24em] text-brand-bone/60 mb-4">Contact</h4>
          <ul className="space-y-3 font-sans text-sm text-brand-bone/85">
            <li className="flex items-start gap-2"><Phone size={14} className="mt-1" /> <a href={BRAND.phoneHref} className="hover:text-brand-bone">{BRAND.phoneDisplay} · ({BRAND.phoneNumeric})</a></li>
            <li className="flex items-start gap-2"><Mail size={14} className="mt-1" /> <a href={BRAND.emailHref} className="hover:text-brand-bone break-all">{BRAND.email}</a></li>
            <li className="flex items-start gap-2"><MapPin size={14} className="mt-1" /> {BRAND.location}</li>
          </ul>
          <h4 className="font-sans text-xs uppercase tracking-[0.24em] text-brand-bone/60 mt-6 mb-3">Service Areas</h4>
          <ul className="space-y-1.5 font-sans text-sm">
            {CITIES.map((c) => (
              <li key={c.slug}>
                <Link
                  to={c.intro ? `/${c.slug}` : '/service-areas'}
                  className="text-brand-bone/75 hover:text-brand-bone transition-colors"
                  data-testid={`footer-city-${c.slug}`}
                >
                  {c.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="border-t border-brand-borderDark">
        <div className="container-x py-5 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-xs font-sans text-brand-bone/55">
          <span>© {new Date().getFullYear()} HearthOak Construction, LLC. All rights reserved.</span>
          <span className="uppercase tracking-wider">License / insurance information available on request</span>
        </div>
      </div>
    </footer>
  );
}
